"use client";

import * as React from "react";
import { MultiLevelCombobox, GenericOption } from "./multi-level-combobox";
import {
  customElementAttributeMappers,
  setCustomElementMetadata,
} from "@/lib/customElements";

// Re-export the types for backward compatibility
interface Option {
  value: string;
  label: string;
  price?: number;
  unit?: string;
  coverage?: number | null;
}
type OptionWithChildren = Option & {
  children: Option[];
};
type OptionWithGrandChildren = Option & {
  children: OptionWithChildren[];
};
export type OptionWithGreatGrandChildren = Option & {
  children: OptionWithGrandChildren[];
};

interface ProductStyleComboboxProps {
  options: OptionWithGreatGrandChildren[];
  name?: string;
  width?: string;
  placeholder?: string;
  emptyMessage?: string;
  defaultTypeValue?: string;
  defaultStyleValue?: string;
  defaultSupplierValue?: string;
  onTypeChange?: (value: string, label: string) => void;
  onStyleChange?: (value: string, label: string, coverage?: number | null) => void;
  onSupplierChange?: (value: string, label: string, price?: number, unit?: string) => void;
  style?: React.CSSProperties;
  compact?: boolean;
}

export function ProductStyleCombobox({
  options,
  width = undefined,
  placeholder = "Select option...",
  emptyMessage = "No option found.",
  defaultTypeValue = "",
  defaultStyleValue = "",
  defaultSupplierValue = "",
  onTypeChange,
  onStyleChange,
  onSupplierChange,
  name = undefined,
  style = {},
  compact = false,
}: Readonly<ProductStyleComboboxProps>) {
  // Convert default values to array format
  const defaultValues = React.useMemo(() => {
    const values: string[] = [];
    
    // Set type value
    if (defaultTypeValue) {
      values[0] = defaultTypeValue;
      
      // Find the type option
      const typeOption = options.find(o => o.value === defaultTypeValue);
      
      if (typeOption) {
        // Find brand based on style or supplier
        let brandValue = "";
        
        // Search through all brands to find the one containing our style/supplier
        for (const brand of typeOption.children) {
          // Check if this brand contains the style
          if (defaultStyleValue) {
            const hasStyle = brand.children.some(s => s.value === defaultStyleValue);
            if (hasStyle) {
              brandValue = brand.value;
              break;
            }
          }
          
          // If no style but supplier is set, check for supplier
          if (!brandValue && defaultSupplierValue) {
            const hasSupplier = brand.children.some(style => 
              style.children.some(sup => sup.value === defaultSupplierValue)
            );
            if (hasSupplier) {
              brandValue = brand.value;
            }
          }
        }
        
        if (brandValue) {
          values[1] = brandValue;
        }
      }
    }
    
    // Set style and supplier values
    if (defaultStyleValue) values[2] = defaultStyleValue;
    if (defaultSupplierValue) values[3] = defaultSupplierValue;
    
    return values;
  }, [defaultTypeValue, defaultStyleValue, defaultSupplierValue, options]);

  // Handle selection changes and map to individual callbacks
  const handleSelectionChange = React.useCallback((
    values: string[],
    labels: string[],
    fullSelection: GenericOption | null
  ) => {
    // Level 0: Type
    if (values[0] && labels[0] && onTypeChange) {
      onTypeChange(values[0], labels[0]);
    }
    
    // Level 2: Style (not brand at level 1)
    if (values[2] && labels[2] && onStyleChange) {
      // Find the style option to get coverage
      const typeOpt = options.find(o => o.value === values[0]);
      const brandOpt = typeOpt?.children.find(o => o.value === values[1]);
      const styleOpt = brandOpt?.children.find(o => o.value === values[2]);
      onStyleChange(values[2], labels[2], styleOpt?.coverage);
    }
    
    // Level 3: Supplier
    if (values[3] && labels[3] && onSupplierChange && fullSelection) {
      onSupplierChange(values[3], labels[3], fullSelection.price, fullSelection.unit);
    }
  }, [options, onTypeChange, onStyleChange, onSupplierChange]);

  // Convert options to GenericOption format
  const genericOptions: GenericOption[] = options as GenericOption[];

  return (
    <MultiLevelCombobox
      options={genericOptions}
      name={name}
      width={width}
      placeholder={placeholder}
      emptyMessage={emptyMessage}
      defaultValues={defaultValues}
      onSelectionChange={handleSelectionChange}
      style={style}
      compact={compact}
      showPriceFilter={true}
      levelConfigs={[
        { placeholder: "Search product type...", backButtonLabel: "Back to product type" },
        { placeholder: "Search brand...", backButtonLabel: "Back to brand" },
        { placeholder: "Search style...", backButtonLabel: "Back to style" },
        { placeholder: "Search supplier...", backButtonLabel: "Back to supplier" }
      ]}
    />
  );
}

const ProductStyleComboboxWebComponentWrapper: React.FC<
  ProductStyleComboboxProps & {
    _domElement?: HTMLElement;
  }
> = (props) => {
  return <ProductStyleCombobox {...props} />;
};

setCustomElementMetadata({
  component: ProductStyleComboboxWebComponentWrapper,
  tagName: "raes-product-style-combobox",
  observedAttributes: {
    name: customElementAttributeMappers.string,
    width: customElementAttributeMappers.string,
    placeholder: customElementAttributeMappers.string,
    "empty-message": customElementAttributeMappers.string,
    "default-value": customElementAttributeMappers.string,
    options: customElementAttributeMappers.json,
  },
});

export { ProductStyleComboboxWebComponentWrapper };
